/**
 * Every FHIR namespace this codebase mints. ADR-0022.
 *
 * The host and each path shape appear exactly once, below. Nothing else in the
 * repository writes a MindForge URL as a literal — a lint plugin and a check
 * script both reject it — because a system URL that differs by scheme or case
 * is a *different* namespace to Medplum's token search, and a resource written
 * under one is invisible to a search under the other with no error.
 */

/** The only domain we control. `mindforge.com` belongs to an unrelated company. */
const HOST = "https://mindforge.health";

declare const brand: unique symbol;

/** `Resource.identifier[].system`. Bare `/identifiers/`, never under `/fhir/`. */
export type IdentifierSystem = string & { readonly [brand]: "IdentifierSystem" };
/** `Coding.system` for a code we define ourselves. */
export type CodeSystemUrl = string & { readonly [brand]: "CodeSystemUrl" };
/** `Extension.url` for an extension we define ourselves. */
export type ExtensionUrl = string & { readonly [brand]: "ExtensionUrl" };
/** `.url` on a canonical resource — PlanDefinition, ActivityDefinition, Questionnaire. */
export type CanonicalUrl = string & { readonly [brand]: "CanonicalUrl" };

export function identifierSystem(name: string): IdentifierSystem {
  return `${HOST}/identifiers/${name}` as IdentifierSystem;
}

export function codeSystemUrl(name: string): CodeSystemUrl {
  return `${HOST}/fhir/CodeSystem/${name}` as CodeSystemUrl;
}

export function extensionUrl(name: string): ExtensionUrl {
  return `${HOST}/fhir/StructureDefinition/${name}` as ExtensionUrl;
}

/** `resourceType` keeps FHIR's own casing: `PlanDefinition`, not `plan-definition`. */
export function canonicalUrl(resourceType: string, name: string): CanonicalUrl {
  return `${HOST}/fhir/${resourceType}/${name}` as CanonicalUrl;
}

/**
 * The base a canonical URL is built on, for the few callers that compose one by
 * hand. Prefer `canonicalUrl`.
 */
export const FHIR_CANONICAL_BASE = `${HOST}/fhir`;

/** The prefix every canonical of one resource type shares, for range queries. */
export function canonicalBase(resourceType: string): string {
  return `${FHIR_CANONICAL_BASE}/${resourceType}`;
}

/**
 * The `system|value` form every identifier search and `ifNoneExist` uses. A bare
 * `identifier=<value>` matches that value in *any* namespace, which is how a
 * lookup silently crosses aggregates.
 */
export function identifierToken(system: IdentifierSystem, value: string): string {
  return `${system}|${value}`;
}

export * from "./foreign";

export const QUESTIONNAIRE_SCORE_SYSTEM = codeSystemUrl("questionnaire-score");

export const PROCEDURE_START_SOURCE_URL = extensionUrl("procedure-start-source");
export const PROCEDURE_END_SOURCE_URL = extensionUrl("procedure-end-source");

export const FINDING_LINEAGE_SYSTEM = codeSystemUrl("finding-lineage");

export const OBSERVATION_CATEGORY_SYSTEM = "http://terminology.hl7.org/CodeSystem/observation-category";

export const CHARTING_OBSERVATION_KIND_SYSTEM = codeSystemUrl("charting-observation-kind");

export const FINDING_STATE_SYSTEM = codeSystemUrl("finding-state");
export const PERFORMANCE_MEASURE_KIND_SYSTEM = codeSystemUrl("response-kind");
export const PERFORMANCE_MEASURE_KIND = "performance-measure";
export const PERFORMANCE_MEASURE_SCORE_KIND = "performance-measure-score";
export const PERFORMANCE_MEASURE_OUTCOME_URL = extensionUrl("performance-measure-outcome");

export const QUESTIONNAIRE_OPTION_SYSTEM = codeSystemUrl("questionnaire-option");
